import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { ArrowLeft, Plus, Trash2, BookOpen, Save } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { companyAPI, masterDataAPI, financeAPI, controllingAPI } from '../services/api';

const POSTING_KEYS_DR = [
  { value: '40', label: '40 - Debit GL' },
  { value: '01', label: '01 - Customer Debit' },
  { value: '21', label: '21 - Vendor Reversal' },
  { value: '25', label: '25 - Vendor Outgoing Payment' },
  { value: '70', label: '70 - Debit Asset' },
  { value: '86', label: '86 - GR/IR Debit' },
];
const POSTING_KEYS_CR = [
  { value: '50', label: '50 - Credit GL' },
  { value: '11', label: '11 - Customer Reversal' },
  { value: '31', label: '31 - Vendor Credit' },
  { value: '15', label: '15 - Customer Incoming Payment' },
  { value: '75', label: '75 - Credit Asset' },
  { value: '96', label: '96 - GR/IR Credit' },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

const newLine = (line_no) => ({
  line_no,
  posting_key: '40',
  debit_credit: 'D',
  gl_account: '',
  amount: '',
  cost_center_id: '',
  profit_center_id: '',
  business_area: '',
  segment_id: '',
  line_text: '',
});

const JournalVoucherCreate = () => {
  const navigate = useNavigate();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Master data
  const [docTypes, setDocTypes] = useState([]);
  const [numberRanges, setNumberRanges] = useState([]);
  const [companyCodes, setCompanyCodes] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [profitCenters, setProfitCenters] = useState([]);
  const [businessAreas, setBusinessAreas] = useState([]);
  const [segments, setSegments] = useState([]);

  // Header
  const [header, setHeader] = useState({
    document_type_id: 'SA',
    company_code_id: '',
    fiscal_year: String(new Date().getFullYear()),
    posting_date: todayIso(),
    document_date: todayIso(),
    currency: 'USD',
    reference: '',
    header_text: '',
  });
  const [lines, setLines] = useState([newLine(1), newLine(2)]);
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line
  useEffect(() => { loadMasterData(); }, []);

  const loadMasterData = async () => {
    try {
      const [dtRes, nrRes, ccRes, currRes, glRes, ccsRes, pcsRes, baRes, segRes] = await Promise.all([
        axios.get(`${backendUrl}/api/fi/document-types`),
        axios.get(`${backendUrl}/api/fi/number-ranges`),
        companyAPI.getCompanyCodes(),
        masterDataAPI.getCurrencies(),
        axios.get(`${backendUrl}/api/gl-accounts`),
        controllingAPI.getCostCenters(),
        controllingAPI.getProfitCenters(),
        financeAPI.getBusinessAreas(),
        fetch(`${backendUrl}/api/finance/segments`).then((r) => r.json()).catch(() => []),
      ]);
      setDocTypes(dtRes.data || []);
      setNumberRanges(nrRes.data || []);
      setCompanyCodes(ccRes.data || []);
      setCurrencies(currRes.data || []);
      setGlAccounts(glRes.data || []);
      setCostCenters(ccsRes.data || []);
      setProfitCenters(pcsRes.data || []);
      setBusinessAreas(baRes.data || []);
      setSegments(Array.isArray(segRes) ? segRes : []);
    } catch (e) {
      toast.error('Failed to load master data');
    }
  };

  const setLine = (idx, field, value) => {
    setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, [field]: value } : l)));
  };

  const addLine = () => {
    setLines((prev) => [...prev, newLine(prev.length + 1)]);
  };
  const removeLine = (idx) => {
    if (lines.length <= 2) {
      toast.error('At least 2 line items are required');
      return;
    }
    setLines((prev) => prev.filter((_, i) => i !== idx).map((l, i) => ({ ...l, line_no: i + 1 })));
  };

  // computed totals
  const totals = lines.reduce(
    (a, l) => {
      const amt = Number(l.amount) || 0;
      if (l.debit_credit === 'D') a.debit += amt;
      else a.credit += amt;
      return a;
    },
    { debit: 0, credit: 0 }
  );
  const balanced = Math.abs(totals.debit - totals.credit) < 0.01 && totals.debit > 0;

  // current doc type's number range info (for header preview)
  const currentDocType = docTypes.find((d) => d.document_type_id === header.document_type_id);
  const currentNR = currentDocType
    ? numberRanges.find(
        (n) =>
          n.number_range_id === currentDocType.number_range_id &&
          n.company_code_id === header.company_code_id &&
          n.fiscal_year === header.fiscal_year
      )
    : null;

  const handlePost = async () => {
    // header validation
    const reqHdr = ['document_type_id', 'company_code_id', 'fiscal_year', 'posting_date', 'document_date', 'currency'];
    for (const f of reqHdr) {
      if (!header[f]) {
        toast.error(`Header field '${f}' is required`); return;
      }
    }
    // line validation
    for (const l of lines) {
      if (!l.gl_account || !l.amount) {
        toast.error(`Line ${l.line_no}: GL account & amount are required`); return;
      }
      if (Number(l.amount) <= 0) {
        toast.error(`Line ${l.line_no}: amount must be > 0`); return;
      }
    }
    if (!balanced) {
      toast.error(`Document not balanced: Dr ${totals.debit.toFixed(2)} ≠ Cr ${totals.credit.toFixed(2)}`);
      return;
    }

    const payload = {
      ...header,
      line_items: lines.map((l) => ({
        line_no: l.line_no,
        posting_key: l.posting_key,
        debit_credit: l.debit_credit,
        gl_account: l.gl_account,
        amount: Number(l.amount),
        cost_center_id: l.cost_center_id || null,
        profit_center_id: l.profit_center_id || null,
        business_area: l.business_area || null,
        segment_id: l.segment_id || null,
        line_text: l.line_text || null,
      })),
    };

    try {
      setSaving(true);
      const r = await axios.post(`${backendUrl}/api/fi/journal-vouchers`, payload);
      toast.success(`Posted as document ${r.data.document_number}`);
      navigate(
        `/finance/journal-vouchers/${r.data.document_number}/${r.data.company_code_id}/${r.data.fiscal_year}`
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Posting failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="jv-create-page">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/finance/journal-vouchers')}>
              <ArrowLeft className="w-4 h-4 mr-1" />Back
            </Button>
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-2">
                <BookOpen className="w-7 h-7 text-orange-600" />
                New Journal Voucher (FB50)
              </h1>
              <p className="text-gray-600 mt-1">Manual G/L posting — header + balanced line items.</p>
            </div>
          </div>
          <Button onClick={handlePost} disabled={saving || !balanced} data-testid="jv-post-btn">
            <Save className="w-4 h-4 mr-1" />{saving ? 'Posting…' : 'Post'}
          </Button>
        </div>

        <Card className="mb-4">
          <CardHeader><CardTitle>Header</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-3 gap-4">
            <div>
              <Label>Document Type *</Label>
              <Select
                value={header.document_type_id}
                onValueChange={(v) => setHeader({ ...header, document_type_id: v })}
              >
                <SelectTrigger data-testid="jv-doc-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {docTypes.map((d) => (
                    <SelectItem key={d.document_type_id} value={d.document_type_id}>
                      {d.document_type_id} - {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Company Code *</Label>
              <Select
                value={header.company_code_id}
                onValueChange={(v) => setHeader({ ...header, company_code_id: v })}
              >
                <SelectTrigger data-testid="jv-cc-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {companyCodes.map((c) => (
                    <SelectItem key={c.company_code_id} value={c.company_code_id}>
                      {c.company_code_id} - {c.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fiscal Year *</Label>
              <Input
                value={header.fiscal_year}
                maxLength={4}
                onChange={(e) => setHeader({ ...header, fiscal_year: e.target.value })}
                data-testid="jv-fy-input"
              />
            </div>
            <div>
              <Label>Posting Date *</Label>
              <Input
                type="date"
                value={header.posting_date}
                onChange={(e) => setHeader({ ...header, posting_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Document Date *</Label>
              <Input
                type="date"
                value={header.document_date}
                onChange={(e) => setHeader({ ...header, document_date: e.target.value })}
              />
            </div>
            <div>
              <Label>Currency *</Label>
              <Select
                value={header.currency}
                onValueChange={(v) => setHeader({ ...header, currency: v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {currencies.map((c) => (
                    <SelectItem key={c.currency_code} value={c.currency_code}>
                      {c.currency_code} - {c.currency_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference</Label>
              <Input
                value={header.reference}
                placeholder="e.g., INV-2026-001"
                onChange={(e) => setHeader({ ...header, reference: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <Label>Header Text</Label>
              <Input
                value={header.header_text}
                onChange={(e) => setHeader({ ...header, header_text: e.target.value })}
                placeholder="Brief description of this posting"
              />
            </div>
            {currentDocType && (
              <div className="col-span-3 text-sm text-gray-600 -mt-2">
                <strong>Number Range Preview:</strong>{' '}
                {currentNR ? (
                  <>
                    {currentDocType.number_range_id} ({currentNR.from_number} - {currentNR.to_number})
                    — next document number ≈ <span className="font-mono">{String(currentNR.current_number).padStart(10, '0')}</span>
                  </>
                ) : (
                  <span className="text-amber-700">
                    No number range configured for {header.company_code_id}/{header.fiscal_year}.
                    Posting will fail until you create one.
                  </span>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="mb-4">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Line Items</CardTitle>
            <Button variant="outline" size="sm" onClick={addLine} data-testid="jv-add-line-btn">
              <Plus className="w-4 h-4 mr-1" />Add Line
            </Button>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">#</TableHead>
                  <TableHead>D/C</TableHead>
                  <TableHead>PK</TableHead>
                  <TableHead>GL Account</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Cost Center</TableHead>
                  <TableHead>Profit Center</TableHead>
                  <TableHead>BA</TableHead>
                  <TableHead>Segment</TableHead>
                  <TableHead>Text</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((l, idx) => (
                  <TableRow key={l.line_no} data-testid={`jv-line-${l.line_no}`}>
                    <TableCell className="font-mono">{l.line_no}</TableCell>
                    <TableCell>
                      <Select value={l.debit_credit} onValueChange={(v) => {
                        setLine(idx, 'debit_credit', v);
                        // auto-pick a sensible posting key
                        setLine(idx, 'posting_key', v === 'D' ? '40' : '50');
                      }}>
                        <SelectTrigger className="w-16" data-testid={`jv-dc-${l.line_no}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="D">Dr</SelectItem>
                          <SelectItem value="C">Cr</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select value={l.posting_key} onValueChange={(v) => setLine(idx, 'posting_key', v)}>
                        <SelectTrigger className="w-32" data-testid={`jv-pk-${l.line_no}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {(l.debit_credit === 'D' ? POSTING_KEYS_DR : POSTING_KEYS_CR).map((k) => (
                            <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select value={l.gl_account} onValueChange={(v) => setLine(idx, 'gl_account', v)}>
                        <SelectTrigger className="w-44" data-testid={`jv-gl-${l.line_no}`}><SelectValue placeholder="Select GL" /></SelectTrigger>
                        <SelectContent>
                          {glAccounts
                            .filter((g) => !header.company_code_id || g.company_code === header.company_code_id)
                            .map((g) => (
                              <SelectItem key={`${g.gl_account}-${g.company_code}`} value={g.gl_account}>
                                {g.gl_account} - {g.account_name}
                              </SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        className="w-32"
                        value={l.amount}
                        onChange={(e) => setLine(idx, 'amount', e.target.value)}
                        data-testid={`jv-amt-${l.line_no}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={l.cost_center_id || 'none'}
                        onValueChange={(v) => setLine(idx, 'cost_center_id', v === 'none' ? '' : v)}
                      >
                        <SelectTrigger className="w-36"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— None —</SelectItem>
                          {costCenters.map((cc) => (
                            <SelectItem key={cc.cost_center_id} value={cc.cost_center_id}>
                              {cc.cost_center_id} - {cc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={l.profit_center_id || 'none'}
                        onValueChange={(v) => setLine(idx, 'profit_center_id', v === 'none' ? '' : v)}
                      >
                        <SelectTrigger className="w-36"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— None —</SelectItem>
                          {profitCenters.map((pc) => (
                            <SelectItem key={pc.profit_center_id} value={pc.profit_center_id}>
                              {pc.profit_center_id} - {pc.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={l.business_area || 'none'}
                        onValueChange={(v) => setLine(idx, 'business_area', v === 'none' ? '' : v)}
                      >
                        <SelectTrigger className="w-28"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— None —</SelectItem>
                          {businessAreas.map((b) => (
                            <SelectItem key={b.business_area} value={b.business_area}>
                              {b.business_area} - {b.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={l.segment_id || 'none'}
                        onValueChange={(v) => setLine(idx, 'segment_id', v === 'none' ? '' : v)}
                      >
                        <SelectTrigger className="w-28"><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— None —</SelectItem>
                          {segments.map((s) => (
                            <SelectItem key={s.segment_id} value={s.segment_id}>
                              {s.segment_id}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        className="w-40"
                        value={l.line_text || ''}
                        onChange={(e) => setLine(idx, 'line_text', e.target.value)}
                        placeholder="Description"
                      />
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => removeLine(idx)} data-testid={`jv-remove-${l.line_no}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-4 grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-gray-500 uppercase">Total Debit</div>
              <div className="text-2xl font-mono font-semibold" data-testid="jv-total-debit">
                {totals.debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Total Credit</div>
              <div className="text-2xl font-mono font-semibold" data-testid="jv-total-credit">
                {totals.credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase">Balance</div>
              <div
                className={`text-2xl font-mono font-semibold ${balanced ? 'text-emerald-600' : 'text-red-600'}`}
                data-testid="jv-balance-status"
              >
                {balanced ? '✓ Balanced' : `Δ ${(totals.debit - totals.credit).toFixed(2)}`}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default JournalVoucherCreate;
